/**
 * Modern React hook for rendering a basketball shot chart using D3 v7.
 * Replaces the legacy d3.chart-based implementation.
 */

import { useCallback, useRef, useEffect } from "react";
import * as d3 from "d3";
import { TS_STEP_FILLS } from "../../../util/reportColors";

// Court constants (in feet)
const COURT_CONSTANTS = {
  BASKET_DIAMETER: 1.5,
  BASKET_PROTRUSION_LENGTH: 4,
  BASKET_WIDTH: 6,
  COURT_LENGTH: 94,
  COURT_WIDTH: 50,
  FREE_THROW_LINE_LENGTH: 19,
  FREE_THROW_CIRCLE_RADIUS: 6,
  KEY_WIDTH: 16,
  KEY_MARK_WIDTH: 0.5,
  RESTRICTED_CIRCLE_RADIUS: 4,
  THREE_POINT_CUTOFF_LENGTH: 14,
  THREE_POINT_RADIUS: 23.75,
  COLLEGE_THREE_POINT_RADIUS: 22.15,
  THREE_POINT_SIDE_RADIUS: 22,
  HEXAGON_RADIUS: 0.75,
  HEXAGON_BIN_VISIBLE_THRESHOLD: 1,
  HEXAGON_RADIUS_THRESHOLD: 2,
  HEXAGON_RADIUS_SIZES: [0, 0.4, 0.6, 0.75],
};

// Hexbin data point interface
interface HexbinPoint {
  x: number;
  y: number;
  made?: number;
  attempts?: number;
}

// Hexbin interface
interface Hexbin extends Array<HexbinPoint> {
  x: number;
  y: number;
  i: number;
  j: number;
  made?: number;
  attempts?: number;
}

interface UseBasketballShotChartOptions {
  width?: number;
  height?: number;
  showCollegeLine?: boolean;
  heatScale?: (value: number) => string;
}

interface UseBasketballShotChartReturn {
  svgRef: React.RefObject<SVGSVGElement>;
  draw: (data: HexbinPoint[]) => void;
}

/**
 * Calculate visible court length (portion of court shown)
 */
function calculateVisibleCourtLength(): number {
  const halfCourtLength = COURT_CONSTANTS.COURT_LENGTH / 2;
  const threePointLength =
    COURT_CONSTANTS.THREE_POINT_RADIUS +
    COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH;
  return threePointLength + (halfCourtLength - threePointLength) / 2;
}

/**
 * Create hexagon path string
 */
function createHexagonPath(radius: number): string {
  const d3_hexbinAngles = d3.range(0, 2 * Math.PI, Math.PI / 3);
  let x0 = 0,
    y0 = 0;
  const points = d3_hexbinAngles.map((angle: number) => {
    const x1 = Math.sin(angle) * radius;
    const y1 = -Math.cos(angle) * radius;
    const ddx = x1 - x0;
    const ddy = y1 - y0;
    x0 = x1;
    y0 = y1;
    return [ddx, ddy];
  });
  return "m" + points.map((p: number[]) => p.join(",")).join("l") + "z";
}

/**
 * Custom hexbin implementation with bin aggregation
 */
function createHexbin(
  visibleCourtLength: number,
  hexagonRadius: number,
  translateX: (d: HexbinPoint) => number,
  translateY: (d: HexbinPoint, visibleCourtLength: number) => number
) {
  const r = hexagonRadius;
  const dx = r * 2 * Math.sin(Math.PI / 3);
  const dy = r * 1.5;

  function hexagon(radius: number): string {
    return createHexagonPath(radius);
  }

  function bin(points: HexbinPoint[]): Hexbin[] {
    const binsById: Record<string, Hexbin> = {};

    points.forEach((point) => {
      const px = translateX(point) / dx;
      const py = translateY(point, visibleCourtLength) / dy;
      let pj = Math.round(py);
      let pi = Math.round(px - (pj & 1 ? 0.5 : 0));
      const py1 = py - pj;

      if (Math.abs(py1) * 3 > 1) {
        const px1 = px - pi - (pj & 1 ? 0.5 : 0);
        const pi2 = pi + (px1 < 0 ? -1 : 1) / 2;
        const pj2 = pj + (py1 < 0 ? -1 : 1);
        const px2 = px - pi2 - (pj2 & 1 ? 0.5 : 0);
        const py2 = py - pj2;
        if (px1 * px1 + py1 * py1 > px2 * px2 + py2 * py2) {
          pi = pi2 + (pj & 1 ? 1 : -1) / 2;
          pj = pj2;
        }
      }

      const id = pi + "-" + pj;
      let hexBin = binsById[id];
      if (!hexBin) {
        hexBin = [] as unknown as Hexbin;
        hexBin.i = pi;
        hexBin.j = pj;
        hexBin.x = (pi + (pj & 1 ? 0.5 : 0)) * dx;
        hexBin.y = pj * dy;
        hexBin.made = 0;
        hexBin.attempts = 0;
        binsById[id] = hexBin;
      }
      hexBin.push(point);

      // Aggregate bin values
      const attempts = point.attempts || 1;
      const made = point.made || 0;
      hexBin.attempts = (hexBin.attempts || 0) + attempts;
      hexBin.made = (hexBin.made || 0) + made;
    });

    // Merge adjacent bins
    const bins_modify = [
      [-1, 0],
      [0, -1],
      [0, 1],
      [1, 0],
      [1, -1],
      [1, 1],
    ];
    for (const key in binsById) {
      const hexBin = binsById[key];
      bins_modify.forEach((bm) => {
        const nkey = hexBin.i + bm[0] + "-" + (hexBin.j + bm[1]);
        const neighbor = binsById[nkey];
        if (neighbor) {
          for (const p of neighbor) {
            hexBin.push(p);
            const attempts = p.attempts || 1;
            const made = p.made || 0;
            hexBin.attempts = (hexBin.attempts || 0) + attempts;
            hexBin.made = (hexBin.made || 0) + made;
          }
        }
      });
    }

    return Object.values(binsById);
  }

  return { bin, hexagon };
}

/**
 * Draw an arc path
 */
function drawArc(
  group: d3.Selection<SVGGElement, unknown, null, undefined>,
  radius: number,
  startAngle: number,
  endAngle: number,
  className: string,
  transform: string,
  dashArray?: string,
  opacity?: number
): void {
  const points = 30;
  const angleScale = d3
    .scaleLinear()
    .domain([0, points - 1])
    .range([startAngle, endAngle]);

  const line = d3
    .lineRadial<number>()
    .curve(d3.curveBasis)
    .radius(radius)
    .angle((_: number, i: number) => angleScale(i) as number);

  const path = group
    .append("path")
    .datum(d3.range(points))
    .attr("class", className)
    .attr("d", line)
    .attr("transform", transform)
    .attr("fill", "none");

  if (dashArray) {
    path.style("stroke-dasharray", dashArray);
  }
  if (opacity !== undefined) {
    path.style("stroke-opacity", opacity);
  }
}

// Default heat scale: maps FG% (made/attempts) to color via efficiency-ratio domain [0.65, 1.35]
function defaultHeatScale(value: number): string {
  const colors = TS_STEP_FILLS;
  const normalized = (value - 0.65) / (1.35 - 0.65);
  const index = Math.min(
    Math.max(Math.floor(normalized * colors.length), 0),
    colors.length - 1
  );
  return colors[index];
}

export function useBasketballShotChart(
  options: UseBasketballShotChartOptions = {}
): UseBasketballShotChartReturn {
  const {
    width = 500,
    showCollegeLine = false,
    heatScale = defaultHeatScale,
  } = options;

  const svgRef = useRef<SVGSVGElement>(null);
  const isInitializedRef = useRef(false);

  const visibleCourtLength = calculateVisibleCourtLength();
  const courtWidth = COURT_CONSTANTS.COURT_WIDTH;

  // Translation functions — stable references (pure functions of their arguments)
  const translateX = useCallback((d: HexbinPoint): number => d.x, []);
  const translateY = useCallback(
    (d: HexbinPoint, vcl: number): number => vcl - d.y,
    []
  );

  // Initialize the chart (draw court, legends)
  useEffect(() => {
    if (!svgRef.current || isInitializedRef.current) return;

    const svg = d3.select(svgRef.current);

    // Set SVG dimensions
    svg
      .attr("class", "shot-chart")
      .attr("width", width)
      .attr("viewBox", `0 0 ${courtWidth} ${visibleCourtLength}`);

    // Draw court
    const court = svg.append("g").attr("class", "shot-chart-court");

    // Key (paint area)
    court
      .append("rect")
      .attr("class", "shot-chart-court-key")
      .attr("x", courtWidth / 2 - COURT_CONSTANTS.KEY_WIDTH / 2)
      .attr("y", visibleCourtLength - COURT_CONSTANTS.FREE_THROW_LINE_LENGTH)
      .attr("width", COURT_CONSTANTS.KEY_WIDTH)
      .attr("height", COURT_CONSTANTS.FREE_THROW_LINE_LENGTH);

    // Baseline
    court
      .append("line")
      .attr("class", "shot-chart-court-baseline")
      .attr("x1", 0)
      .attr("y1", visibleCourtLength)
      .attr("x2", courtWidth)
      .attr("y2", visibleCourtLength);

    // College three point line (if enabled) — arc clipped at corners + straight corner lines
    if (showCollegeLine) {
      const collegeTpAngle = Math.asin(
        COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS / COURT_CONSTANTS.COLLEGE_THREE_POINT_RADIUS
      );
      drawArc(
        court,
        COURT_CONSTANTS.COLLEGE_THREE_POINT_RADIUS,
        -collegeTpAngle,
        collegeTpAngle,
        "shot-chart-court-college-3pt-line",
        `translate(${courtWidth / 2}, ${
          visibleCourtLength -
          COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH -
          COURT_CONSTANTS.BASKET_DIAMETER / 2
        })`,
        "1.5,0.25",
        0.4
      );

      const collegeCutoff =
        COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH +
        COURT_CONSTANTS.BASKET_DIAMETER / 2 +
        Math.sqrt(
          COURT_CONSTANTS.COLLEGE_THREE_POINT_RADIUS ** 2 -
          COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS ** 2
        );
      [1, -1].forEach((n) => {
        court
          .append("line")
          .attr("class", "shot-chart-court-college-3pt-line")
          .attr("x1", courtWidth / 2 + COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS * n)
          .attr("y1", visibleCourtLength - collegeCutoff)
          .attr("x2", courtWidth / 2 + COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS * n)
          .attr("y2", visibleCourtLength)
          .style("stroke-dasharray", "1.5,0.25")
          .style("stroke-opacity", 0.4);
      });
    }

    // NBA three point arc
    const tpAngle = Math.asin(
      COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS / COURT_CONSTANTS.THREE_POINT_RADIUS
    );
    drawArc(
      court,
      COURT_CONSTANTS.THREE_POINT_RADIUS,
      -tpAngle,
      tpAngle,
      "shot-chart-court-3pt-line",
      `translate(${courtWidth / 2}, ${
        visibleCourtLength -
        COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH -
        COURT_CONSTANTS.BASKET_DIAMETER / 2
      })`
    );

    // Three point corners — compute exact cutoff where arc meets corner lines
    const nbaCutoff =
      COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH +
      COURT_CONSTANTS.BASKET_DIAMETER / 2 +
      Math.sqrt(
        COURT_CONSTANTS.THREE_POINT_RADIUS ** 2 -
        COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS ** 2
      );
    [1, -1].forEach((n) => {
      court
        .append("line")
        .attr("class", "shot-chart-court-3pt-line")
        .attr("x1", courtWidth / 2 + COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS * n)
        .attr("y1", visibleCourtLength - nbaCutoff)
        .attr("x2", courtWidth / 2 + COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS * n)
        .attr("y2", visibleCourtLength);
    });

    // Restricted area
    drawArc(
      court,
      COURT_CONSTANTS.RESTRICTED_CIRCLE_RADIUS,
      -Math.PI / 2,
      Math.PI / 2,
      "shot-chart-court-restricted-area",
      `translate(${courtWidth / 2}, ${
        visibleCourtLength -
        COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH -
        COURT_CONSTANTS.BASKET_DIAMETER / 2
      })`
    );

    // Free throw circle (top - solid)
    drawArc(
      court,
      COURT_CONSTANTS.FREE_THROW_CIRCLE_RADIUS,
      -Math.PI / 2,
      Math.PI / 2,
      "shot-chart-court-ft-circle-top",
      `translate(${courtWidth / 2}, ${
        visibleCourtLength - COURT_CONSTANTS.FREE_THROW_LINE_LENGTH
      })`
    );

    // Free throw circle (bottom - dashed)
    drawArc(
      court,
      COURT_CONSTANTS.FREE_THROW_CIRCLE_RADIUS,
      Math.PI / 2,
      1.5 * Math.PI,
      "shot-chart-court-ft-circle-bottom",
      `translate(${courtWidth / 2}, ${
        visibleCourtLength - COURT_CONSTANTS.FREE_THROW_LINE_LENGTH
      })`
    );

    // Key marks
    [7, 8, 11, 14].forEach((mark) => {
      [1, -1].forEach((n) => {
        court
          .append("line")
          .attr("class", "shot-chart-court-key-mark")
          .attr(
            "x1",
            courtWidth / 2 +
              (COURT_CONSTANTS.KEY_WIDTH / 2) * n +
              COURT_CONSTANTS.KEY_MARK_WIDTH * n
          )
          .attr("y1", visibleCourtLength - mark)
          .attr("x2", courtWidth / 2 + (COURT_CONSTANTS.KEY_WIDTH / 2) * n)
          .attr("y2", visibleCourtLength - mark);
      });
    });

    // Backboard
    court
      .append("line")
      .attr("class", "shot-chart-court-backboard")
      .attr("x1", courtWidth / 2 - COURT_CONSTANTS.BASKET_WIDTH / 2)
      .attr("y1", visibleCourtLength - COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH)
      .attr("x2", courtWidth / 2 + COURT_CONSTANTS.BASKET_WIDTH / 2)
      .attr("y2", visibleCourtLength - COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH);

    // Hoop
    court
      .append("circle")
      .attr("class", "shot-chart-court-hoop")
      .attr("cx", courtWidth / 2)
      .attr(
        "cy",
        visibleCourtLength -
          COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH -
          COURT_CONSTANTS.BASKET_DIAMETER / 2
      )
      .attr("r", COURT_CONSTANTS.BASKET_DIAMETER / 2);

    // Create hexagon layer group
    svg.append("g").attr("class", "hexagons-group");

    // Draw legends
    drawLegends(svg as any, courtWidth, visibleCourtLength, heatScale);

    isInitializedRef.current = true;
  }, [width, showCollegeLine, courtWidth, visibleCourtLength, heatScale]);

  // Draw function to render hexbin data
  const draw = useCallback(
    (data: HexbinPoint[]) => {
      if (!svgRef.current || !isInitializedRef.current) return;

      const svg = d3.select(svgRef.current);
      const hexagonsGroup = svg.select<SVGGElement>(".hexagons-group");

      // Clear existing hexagons
      hexagonsGroup.selectAll(".shot-chart-hexagon").remove();

      if (data.length === 0) return;

      const hexbin = createHexbin(
        visibleCourtLength,
        COURT_CONSTANTS.HEXAGON_RADIUS,
        translateX,
        translateY
      );

      // Bin the data
      const allBins = hexbin.bin(data);

      // Filter bins by visibility threshold
      const visibleBins: Hexbin[] = [];
      const hexbinQuantities: number[] = [];

      allBins.forEach((bin) => {
        const numPts = bin.attempts || 0;
        if (numPts > COURT_CONSTANTS.HEXAGON_BIN_VISIBLE_THRESHOLD) {
          visibleBins.push(bin);
        }
        if (numPts > COURT_CONSTANTS.HEXAGON_RADIUS_THRESHOLD) {
          hexbinQuantities.push(numPts);
        }
      });

      // Create radius scale
      const radiusScale = d3
        .scaleQuantile()
        .domain(hexbinQuantities)
        .range(COURT_CONSTANTS.HEXAGON_RADIUS_SIZES);

      // Add clip path
      const clipPath = svg.select("#shot-chart-clip");
      if (clipPath.empty()) {
        svg
          .append("clipPath")
          .attr("id", "shot-chart-clip")
          .append("rect")
          .attr("class", "shot-chart-mesh")
          .attr("width", courtWidth)
          .attr("height", visibleCourtLength);
      }

      // Create container with clip path
      let clippedGroup = hexagonsGroup.select<SVGGElement>(".clipped-hexagons");
      if (clippedGroup.empty()) {
        clippedGroup = hexagonsGroup
          .append("g")
          .attr("class", "clipped-hexagons")
          .attr("clip-path", "url(#shot-chart-clip)");
      }

      // Draw hexagons
      clippedGroup
        .selectAll(".shot-chart-hexagon")
        .data(visibleBins)
        .enter()
        .append("path")
        .attr("class", "shot-chart-hexagon")
        .attr("transform", (d: Hexbin) => `translate(${d.x}, ${d.y})`)
        .attr("d", (d: Hexbin) => {
          const val = radiusScale(d.attempts || 0);
          if (val > 0) return hexbin.hexagon(val);
          return "";
        })
        .style("fill", (d: Hexbin) => {
          const fgPct = d.attempts ? (d.made || 0) / d.attempts : 0;
          return heatScale(fgPct);
        });
    },
    [courtWidth, visibleCourtLength, heatScale, translateX, translateY]
  );

  // Cleanup on unmount — remove D3 elements so they don't accumulate across open/close cycles
  useEffect(() => {
    const svg = svgRef.current;
    return () => {
      if (svg) d3.select(svg).selectAll("*").remove();
      isInitializedRef.current = false;
    };
  }, []);

  return { svgRef, draw };
}

/**
 * Draw the color and size legends
 */
function drawLegends(
  svg: any,
  courtWidth: number,
  visibleCourtLength: number,
  heatScale: (value: number) => string
): void {
  const hexagonRadiusSizes = COURT_CONSTANTS.HEXAGON_RADIUS_SIZES;
  const keyWidth = COURT_CONSTANTS.KEY_WIDTH;
  const basketProtrusionLength = COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH;
  const threePointSideRadius = COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS;

  // Get heat range colors
  const heatRange = TS_STEP_FILLS;
  const largestHexagonRadius = hexagonRadiusSizes[hexagonRadiusSizes.length - 1];

  // Color legend position
  const colorXMid =
    courtWidth -
    (threePointSideRadius - keyWidth / 2) / 2 -
    (courtWidth / 2 - threePointSideRadius);
  const colorXStart = colorXMid - heatRange.length * largestHexagonRadius;
  const colorYStart = visibleCourtLength - basketProtrusionLength / 3;

  // Color legend
  const colorLegend = svg.append("g").attr("class", "legend");

  colorLegend
    .append("text")
    .attr("class", "legend-title")
    .attr("x", colorXMid)
    .attr("y", colorYStart - largestHexagonRadius * 2)
    .attr("text-anchor", "middle")
    .text("Efficiency");

  colorLegend
    .append("text")
    .attr("x", colorXStart)
    .attr("y", colorYStart)
    .attr("text-anchor", "end")
    .text("< avg");

  colorLegend
    .append("text")
    .attr("x", colorXStart + heatRange.length * 2 * largestHexagonRadius)
    .attr("y", colorYStart)
    .attr("text-anchor", "start")
    .text("> avg");

  colorLegend
    .selectAll("path.color-legend")
    .data(heatRange)
    .enter()
    .append("path")
    .attr("class", "color-legend")
    .attr("d", createHexagonPath(largestHexagonRadius))
    .attr(
      "transform",
      (_: string, i: number) =>
        `translate(${colorXStart + (1 + i * 2) * largestHexagonRadius}, ${colorYStart})`
    )
    .style("fill", (d: string) => d);

  // Size legend
  const sizeRange = hexagonRadiusSizes.slice(-3);
  let sizeLengendWidth = 0;
  for (let i = 0; i < sizeRange.length; ++i) {
    sizeLengendWidth += sizeRange[i] * 2;
  }
  const sizeXMid =
    (threePointSideRadius - keyWidth / 2) / 2 +
    (courtWidth / 2 - threePointSideRadius);
  let sizeXStart = sizeXMid - sizeLengendWidth / 2;
  const sizeYStart = visibleCourtLength - basketProtrusionLength / 3;

  const sizeLegend = svg.append("g").attr("class", "legend");

  sizeLegend
    .append("text")
    .attr("class", "legend-title")
    .attr("x", sizeXMid)
    .attr("y", sizeYStart - largestHexagonRadius * 2)
    .attr("text-anchor", "middle")
    .text("Frequency");

  sizeLegend
    .append("text")
    .attr("x", sizeXStart)
    .attr("y", sizeYStart)
    .attr("text-anchor", "end")
    .text("low");

  sizeLegend
    .selectAll("path.size-legend")
    .data(sizeRange)
    .enter()
    .append("path")
    .attr("class", "size-legend")
    .attr("d", (d: number) => createHexagonPath(d))
    .attr("transform", (d: number) => {
      sizeXStart += d * 2;
      return `translate(${sizeXStart - d}, ${sizeYStart})`;
    })
    .style("fill", "#999");

  sizeLegend
    .append("text")
    .attr("x", sizeXStart)
    .attr("y", sizeYStart)
    .attr("text-anchor", "start")
    .text("high");
}

export default useBasketballShotChart;
